import React from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { weekplan, grey } from '../../styles/tokens';

// Jours de la semaine
const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const DAYS_FULL = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

// Types de repas
const MEAL_KEYS = ['breakfast', 'morningSnack', 'lunch', 'snack', 'dinner'];
const MEAL_LABELS = {
  breakfast: 'Petit-dej',
  morningSnack: 'Encas AM',
  lunch: 'Dejeuner',
  snack: 'Encas PM',
  dinner: 'Diner',
};

// Types de jour
const DAY_TYPES = [
  { value: 'OFF', label: 'OFF', color: weekplan.dayType.OFF },
  { value: 'ON', label: 'ON', color: weekplan.dayType.ON },
  { value: 'INTENSIF', label: 'INT', color: weekplan.dayType.INTENSIF },
];

/**
 * Cellule de repas cliquable
 */
const MealCell = ({ meal, onClick, onRemove, mealLabel, showLabel = false }) => {
  const isEmpty = !meal || !meal._id;
  const recipeName = meal?.recipeName || meal?.name || '';

  // Tronquer le nom si trop long
  const displayName = recipeName.length > 18
    ? recipeName.substring(0, 18) + '...'
    : recipeName;

  if (isEmpty) {
    return (
      <Box
        onClick={onClick}
        sx={{
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: showLabel ? 'flex-start' : 'center',
          gap: 1,
          backgroundColor: weekplan.emptyCell.bg,
          border: `1px dashed ${weekplan.emptyCell.border}`,
          borderRadius: 1,
          cursor: 'pointer',
          px: showLabel ? 1.5 : 0,
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: weekplan.hoverCell.bg,
            borderColor: weekplan.hoverCell.border,
            borderStyle: 'solid',
          },
        }}
      >
        {showLabel && (
          <Typography
            variant="caption"
            sx={{ color: grey[500], fontSize: '0.75rem', minWidth: 70 }}
          >
            {mealLabel}
          </Typography>
        )}
        <AddIcon sx={{ color: grey[400], fontSize: 20 }} />
      </Box>
    );
  }

  return (
    <Tooltip title={recipeName} arrow placement="top">
      <Box
        sx={{
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: weekplan.filledCell.bg,
          border: `1px solid ${weekplan.filledCell.border}`,
          borderRadius: 1,
          px: 1.5,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: weekplan.hoverCell.bg,
            borderColor: weekplan.hoverCell.border,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          {showLabel && (
            <Typography
              variant="caption"
              sx={{ color: grey[500], fontSize: '0.75rem', minWidth: 70, flexShrink: 0 }}
            >
              {mealLabel}
            </Typography>
          )}
          <Typography
            variant="body2"
            onClick={onClick}
            sx={{
              flex: 1,
              fontSize: '0.8rem',
              color: weekplan.filledCell.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          sx={{
            p: 0.25,
            ml: 0.5,
            color: grey[400],
            '&:hover': { color: grey[600] },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Tooltip>
  );
};

/**
 * Selecteur compact pour le type de jour
 */
const DayTypeSelect = ({ value, onChange, fullWidth = false }) => {
  const currentType = DAY_TYPES.find(t => t.value === value) || DAY_TYPES[0];

  return (
    <Select
      value={value || 'OFF'}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      fullWidth={fullWidth}
      sx={{
        minWidth: fullWidth ? 'auto' : 70,
        height: 28,
        fontSize: '0.7rem',
        fontWeight: 600,
        backgroundColor: currentType.color.bg,
        color: currentType.color.text,
        '& .MuiSelect-select': {
          py: 0.5,
          px: 1,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      }}
    >
      {DAY_TYPES.map((type) => (
        <MenuItem key={type.value} value={type.value}>
          <Chip
            label={type.label}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 600,
              backgroundColor: type.color.chip,
              color: type.color.text,
            }}
          />
        </MenuItem>
      ))}
    </Select>
  );
};

/**
 * Indicateur de calories
 */
const CalorieDisplay = ({ current, target, loading, inline = false }) => {
  if (loading) {
    return <CircularProgress size={14} thickness={4} />;
  }

  const percentage = target ? (current / target) * 100 : 0;
  let color = weekplan.calories.onTarget;
  if (percentage < 90) color = weekplan.calories.underTarget;
  if (percentage > 110) color = weekplan.calories.overTarget;

  if (inline) {
    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.8rem',
          fontWeight: 600,
          color: color,
        }}
      >
        {current || 0}{target ? ` / ${target}` : ''} kcal
      </Typography>
    );
  }

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: color,
        }}
      >
        {current || 0}
      </Typography>
      {target && (
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.6rem',
            color: grey[400],
          }}
        >
          /{target}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Vue mobile - Cartes empilées par jour
 */
const MobileView = ({
  dayPlans,
  selectedClient,
  calculatedCalories,
  loadingCalories,
  getTargetCaloriesForDay,
  onMealClick,
  onMealRemove,
  onDayTypeChange,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {DAYS_FULL.map((day, dayIndex) => {
        const dayPlan = dayPlans[dayIndex] || { meals: {}, dayType: 'OFF' };

        return (
          <Box
            key={day}
            sx={{
              border: `1px solid ${grey[200]}`,
              borderRadius: 2,
              backgroundColor: '#fff',
              overflow: 'hidden',
            }}
          >
            {/* Header du jour */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                backgroundColor: weekplan.header.bg,
                borderBottom: `1px solid ${grey[200]}`,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: weekplan.header.text }}
              >
                {day}
              </Typography>
              <DayTypeSelect
                value={dayPlan.dayType}
                onChange={(value) => onDayTypeChange(dayIndex, value)}
              />
            </Box>

            {/* Calories */}
            {selectedClient && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 1,
                  px: 1.5,
                  backgroundColor: grey[50],
                  borderBottom: `1px solid ${grey[100]}`,
                }}
              >
                <CalorieDisplay
                  current={calculatedCalories[dayIndex]}
                  target={getTargetCaloriesForDay(dayPlan.dayType, selectedClient)}
                  loading={loadingCalories[dayIndex]}
                  inline
                />
              </Box>
            )}

            {/* Repas */}
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {MEAL_KEYS.map((mealKey) => (
                <MealCell
                  key={mealKey}
                  meal={dayPlan.meals?.[mealKey]}
                  mealLabel={MEAL_LABELS[mealKey]}
                  showLabel
                  onClick={() => onMealClick(dayIndex, mealKey)}
                  onRemove={() => onMealRemove(dayIndex, mealKey)}
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * Vue desktop - Tableau
 */
const DesktopView = ({
  dayPlans,
  selectedClient,
  calculatedCalories,
  loadingCalories,
  getTargetCaloriesForDay,
  onMealClick,
  onMealRemove,
  onDayTypeChange,
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'auto',
        border: `1px solid ${grey[200]}`,
        borderRadius: 2,
        backgroundColor: '#fff',
      }}
    >
      {/* Table */}
      <Box
        component="table"
        sx={{
          width: '100%',
          minWidth: 700,
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        {/* Header - Jours */}
        <Box component="thead">
          <Box component="tr">
            {/* Cellule vide pour la colonne des labels */}
            <Box
              component="th"
              sx={{
                width: 80,
                p: 1,
                backgroundColor: weekplan.header.bg,
                borderBottom: `2px solid ${weekplan.header.border}`,
              }}
            />
            {/* Jours */}
            {DAYS.map((day) => (
              <Box
                key={day}
                component="th"
                sx={{
                  p: 1,
                  backgroundColor: weekplan.header.bg,
                  borderBottom: `2px solid ${weekplan.header.border}`,
                  textAlign: 'center',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: weekplan.header.text,
                    fontSize: '0.8rem',
                  }}
                >
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box component="tbody">
          {/* Ligne Type de jour */}
          <Box component="tr">
            <Box
              component="td"
              sx={{
                p: 1,
                backgroundColor: weekplan.mealLabel.bg,
                borderRight: `1px solid ${grey[200]}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  color: weekplan.mealLabel.text,
                  fontSize: '0.7rem',
                }}
              >
                Type
              </Typography>
            </Box>
            {dayPlans.map((dayPlan, dayIndex) => (
              <Box
                key={`type-${dayIndex}`}
                component="td"
                sx={{
                  p: 0.5,
                  textAlign: 'center',
                  borderBottom: `1px solid ${grey[100]}`,
                }}
              >
                <DayTypeSelect
                  value={dayPlan.dayType}
                  onChange={(value) => onDayTypeChange(dayIndex, value)}
                />
              </Box>
            ))}
          </Box>

          {/* Ligne Calories */}
          <Box component="tr">
            <Box
              component="td"
              sx={{
                p: 1,
                backgroundColor: weekplan.mealLabel.bg,
                borderRight: `1px solid ${grey[200]}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  color: weekplan.mealLabel.text,
                  fontSize: '0.7rem',
                }}
              >
                kcal
              </Typography>
            </Box>
            {dayPlans.map((dayPlan, dayIndex) => (
              <Box
                key={`cal-${dayIndex}`}
                component="td"
                sx={{
                  p: 0.5,
                  textAlign: 'center',
                  borderBottom: `2px solid ${grey[200]}`,
                }}
              >
                <CalorieDisplay
                  current={calculatedCalories[dayIndex]}
                  target={getTargetCaloriesForDay(dayPlan.dayType, selectedClient)}
                  loading={loadingCalories[dayIndex]}
                />
              </Box>
            ))}
          </Box>

          {/* Lignes des repas */}
          {MEAL_KEYS.map((mealKey) => (
            <Box key={mealKey} component="tr">
              {/* Label du repas */}
              <Box
                component="td"
                sx={{
                  p: 1,
                  backgroundColor: weekplan.mealLabel.bg,
                  borderRight: `1px solid ${grey[200]}`,
                  borderBottom: `1px solid ${grey[100]}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    color: weekplan.mealLabel.text,
                    fontSize: '0.7rem',
                  }}
                >
                  {MEAL_LABELS[mealKey]}
                </Typography>
              </Box>

              {/* Cellules des repas */}
              {dayPlans.map((dayPlan, dayIndex) => (
                <Box
                  key={`${mealKey}-${dayIndex}`}
                  component="td"
                  sx={{
                    p: 0.5,
                    borderBottom: `1px solid ${grey[100]}`,
                  }}
                >
                  <MealCell
                    meal={dayPlan.meals?.[mealKey]}
                    onClick={() => onMealClick(dayIndex, mealKey)}
                    onRemove={() => onMealRemove(dayIndex, mealKey)}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Composant principal - Vue tableau du planning
 * Responsive: tableau sur desktop, cartes sur mobile
 */
const WeekPlanTable = ({
  dayPlans,
  setDayPlans,
  recipes,
  selectedClient,
  calculatedCalories,
  loadingCalories,
  getTargetCaloriesForDay,
  onMealClick,
  onMealRemove,
  onDayTypeChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const commonProps = {
    dayPlans,
    selectedClient,
    calculatedCalories,
    loadingCalories,
    getTargetCaloriesForDay,
    onMealClick,
    onMealRemove,
    onDayTypeChange,
  };

  return isMobile ? (
    <MobileView {...commonProps} />
  ) : (
    <DesktopView {...commonProps} />
  );
};

export default WeekPlanTable;
